import { useEffect, useMemo, useRef, useState } from 'react'
import { Activity, ClipboardCheck, Clock3, ListTodo, Star } from 'lucide-react'
import { watchTaskAuditEvents } from '../planner/cloud'
import type { TaskAuditEvent } from '../planner/types'
import type { Client, ClientStatus } from '../types'
import { CLIENT_STATUS_OPTIONS } from '../types'
import { syncLatestFollowUpTask } from '../workspace/safeCloud'
import {
  dailyActions,
  dailySummaryText,
  dailyTaskEvents,
  localDateKey,
  taskAuditDetails,
} from '../workspace/safeUtils'

const timeFormatter = new Intl.DateTimeFormat('ru-RU', {
  hour: '2-digit',
  minute: '2-digit',
})

const nextContactFormatter = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
})

function statusLabel(status: ClientStatus): string {
  if (status === 'contacted') return 'Связались'
  return CLIENT_STATUS_OPTIONS.find((item) => item.value === status)?.label ?? status
}

export function FollowUpTaskSync({ userId, clients }: { userId: string; clients: Client[] }) {
  const syncingRef = useRef(new Set<string>())

  useEffect(() => {
    let cancelled = false

    async function syncTasks() {
      for (const client of clients) {
        const interaction = client.interactions[0]
        if (!interaction?.nextContactAt || cancelled) continue
        const key = `${client.id}:${interaction.id}`
        if (syncingRef.current.has(key)) continue
        syncingRef.current.add(key)
        try {
          await syncLatestFollowUpTask(userId, client)
        } catch {
          // Синхронизация повторится при следующем обновлении карточки.
        } finally {
          syncingRef.current.delete(key)
        }
      }
    }

    void syncTasks()
    return () => {
      cancelled = true
    }
  }, [clients, userId])

  return null
}

export function ActiveClientsPanel({
  clients,
  onOpenClient,
  onShowAll,
}: {
  clients: Client[]
  onOpenClient: (client: Client) => void
  onShowAll: () => void
}) {
  const activeClients = useMemo(
    () => clients
      .filter((client) => client.important && client.status !== 'not_interested')
      .sort((left, right) => {
        const leftDate = left.nextContactAt
          ? new Date(left.nextContactAt).getTime()
          : Number.MAX_SAFE_INTEGER
        const rightDate = right.nextContactAt
          ? new Date(right.nextContactAt).getTime()
          : Number.MAX_SAFE_INTEGER
        return leftDate - rightDate || left.company.localeCompare(right.company, 'ru')
      })
      .slice(0, 6),
    [clients],
  )

  return (
    <section className="active-clients workspace-card" aria-label="Клиенты в работе">
      <div className="active-clients-heading">
        <div>
          <span className="eyebrow"><Star size={14} /> Ручной фокус</span>
          <h2>Клиенты в работе</h2>
          <p>Компании, которым вы сами дали приоритет.</p>
        </div>
        <button className="text-button" type="button" onClick={onShowAll}>
          Все · {activeClients.length}
        </button>
      </div>

      {activeClients.length ? (
        <div className="active-client-list">
          {activeClients.map((client) => (
            <button type="button" key={client.id} onClick={() => onOpenClient(client)}>
              <Star size={16} fill="currentColor" />
              <div>
                <strong>{client.company || 'Без названия'}</strong>
                <span>{client.taxId ? `ИНН ${client.taxId}` : client.contactPerson || 'Без ИНН'}</span>
              </div>
              <small>
                {client.nextContactAt
                  ? nextContactFormatter.format(new Date(client.nextContactAt))
                  : statusLabel(client.status)}
              </small>
            </button>
          ))}
        </div>
      ) : (
        <div className="active-clients-empty">
          Добавляйте важные компании в работу из карточки клиента.
        </div>
      )}
    </section>
  )
}

export function DailySummaryPanel({
  userId,
  clients,
  onOpenClient,
}: {
  userId: string
  clients: Client[]
  onOpenClient: (client: Client) => void
}) {
  const [selectedDate, setSelectedDate] = useState(() => localDateKey(new Date()))
  const [taskEvents, setTaskEvents] = useState<TaskAuditEvent[]>([])
  const [taskEventsError, setTaskEventsError] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => watchTaskAuditEvents(
    userId,
    (items) => {
      setTaskEvents(items)
      setTaskEventsError(false)
    },
    () => setTaskEventsError(true),
  ), [userId])

  const actions = useMemo(
    () => dailyActions(clients, selectedDate),
    [clients, selectedDate],
  )
  const events = useMemo(
    () => dailyTaskEvents(taskEvents, selectedDate),
    [selectedDate, taskEvents],
  )
  const timeline = useMemo(
    () => [
      ...actions.map((item) => ({
        kind: 'contact' as const,
        occurredAt: item.interaction.createdAt,
        item,
      })),
      ...events.map((event) => ({
        kind: 'task' as const,
        occurredAt: event.occurredAt,
        event,
      })),
    ].sort(
      (left, right) =>
        new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime(),
    ),
    [actions, events],
  )
  const followUps = actions.filter((item) => Boolean(item.interaction.nextContactAt)).length

  async function copySummary() {
    try {
      await navigator.clipboard.writeText(dailySummaryText(actions, selectedDate, events))
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      setCopied(false)
    }
  }

  return (
    <section className="daily-summary workspace-card" aria-label="Итоги рабочего дня">
      <div className="daily-summary-heading">
        <div>
          <span className="eyebrow"><Activity size={14} /> Журнал действий</span>
          <h2>Итоги за день</h2>
          <p>Контакты и изменения задач собраны по фактическому времени.</p>
        </div>
        <div className="daily-summary-actions">
          <input
            type="date"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
            aria-label="Дата отчёта"
          />
          <button className="button secondary" type="button" onClick={() => void copySummary()}>
            <ClipboardCheck size={16} /> {copied ? 'Скопировано' : 'Скопировать'}
          </button>
        </div>
      </div>

      <div className="daily-summary-stats">
        <article><strong>{timeline.length}</strong><span>всего действий</span></article>
        <article><strong>{actions.length}</strong><span>контактов</span></article>
        <article><strong>{events.length}</strong><span>событий задач</span></article>
        <article><strong>{followUps}</strong><span>повторных контактов</span></article>
      </div>

      {taskEventsError ? (
        <div className="daily-summary-warning" role="status">
          Журнал задач сейчас недоступен. Контакты показаны, но отчёт может быть неполным.
        </div>
      ) : null}

      <div className="daily-activity-list workspace-timeline">
        {timeline.length ? timeline.map((entry) => {
          if (entry.kind === 'task') {
            return (
              <article className="daily-task-event" key={entry.event.id}>
                <time>{timeFormatter.format(new Date(entry.event.occurredAt))}</time>
                <span className="daily-summary-status task"><ListTodo size={13} /> Задача</span>
                <div>
                  <strong>{entry.event.taskTitle}</strong>
                  <ul>{taskAuditDetails(entry.event).map((detail) => <li key={detail}>{detail}</li>)}</ul>
                </div>
                <span className="daily-summary-next">
                  <Clock3 size={13} /> Срок {nextContactFormatter.format(new Date(entry.event.dueAt))}
                </span>
              </article>
            )
          }

          const client = clients.find((item) => item.id === entry.item.clientId)
          return (
            <button
              type="button"
              className="daily-contact-event"
              key={`${entry.item.clientId}-${entry.item.interaction.id}`}
              onClick={() => client && onOpenClient(client)}
            >
              <time>{timeFormatter.format(new Date(entry.item.interaction.createdAt))}</time>
              <span className={`daily-summary-status ${entry.item.interaction.status}`}>
                {statusLabel(entry.item.interaction.status)}
              </span>
              <div>
                <strong>{entry.item.company}</strong>
                <small>{entry.item.taxId ? `ИНН ${entry.item.taxId}` : 'ИНН не указан'}</small>
                <p>{entry.item.interaction.outcome || 'Результат не указан'}</p>
              </div>
              <span className="daily-summary-next">
                {entry.item.interaction.nextContactAt
                  ? <><Clock3 size={13} /> {nextContactFormatter.format(new Date(entry.item.interaction.nextContactAt))}</>
                  : 'Без следующего контакта'}
              </span>
            </button>
          )
        }) : (
          <div className="daily-summary-empty">За выбранный день действий не зафиксировано.</div>
        )}
      </div>
    </section>
  )
}
